import React, { useState, useRef, useEffect } from "react";
import "../../Styles/Mp3Table.css";
import { useNavigate } from "react-router-dom";

// Import your actual audio files from assets
import song1 from "../../essets/audio1.mp3";
import song2 from "../../essets/audio2.mp3";
import rallySong from "../../essets/inraudio.mpeg";

function AdminMp3Table() {
  const navigate = useNavigate();

  const initialData = [
    {
      id: 1,
      title: "Campaign Song 1",
      fileName: "song1.mp3",
      audioSrc: song1,
      duration: "03:42",
      dateAdded: "20-07-2026",
      status: "Active",
      plays: 1240,
      downloads: 420,
      color: "mp3123-cover-green",
    },
    {
      id: 2,
      title: "Campaign Song 2",
      fileName: "song2.mp3",
      audioSrc: song2,
      duration: "04:15",
      dateAdded: "22-05-2026",
      status: "Active",
      plays: 980,
      downloads: 310,
      color: "mp3123-cover-blue",
    },
    {
      id: 3,
      title: "Election Rally Song",
      fileName: "rally_song.mp3",
      audioSrc: rallySong,
      duration: "03:55",
      dateAdded: "15-05-2026",
      status: "Active",
      plays: 1036,
      downloads: 398,
      color: "mp3123-cover-orange",
    },
  ];

  const [songs, setSongs] = useState(initialData);

  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [status, setStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const audioRefs = useRef({});

  const totalPages = 8;

  const resetFilters = () => {
    setSearch("");
    setDateFilter("All Dates");
    setStatus("All Status");
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- AUDIO PLAYBACK LOGIC ----------
  const getAudioEl = (id) => {
    if (!audioRefs.current[id]) {
      audioRefs.current[id] = new Audio();
    }
    return audioRefs.current[id];
  };

  const togglePlay = (item) => {
    const audio = getAudioEl(item.id);

    // Pause any other currently playing song first
    if (playingId && playingId !== item.id) {
      const prevAudio = audioRefs.current[playingId];
      if (prevAudio) {
        prevAudio.pause();
      }
    }

    if (playingId === item.id) {
      // Currently playing this one -> pause it
      audio.pause();
      setPlayingId(null);
    } else {
      // Start playing this one
      if (audio.src !== item.audioSrc) {
        audio.src = item.audioSrc;
      }

      audio.play().catch((err) => {
        console.error("Audio playback failed:", err);
      });

      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };

      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
      };

      audio.onended = () => {
        setPlayingId(null);
        setCurrentTime(0);
      };

      setPlayingId(item.id);
      setCurrentTime(0);
    }
  };

  // ---------- DOWNLOAD LOGIC ----------
  const handleDownload = async (item) => {
    try {
      const response = await fetch(item.audioSrc);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.fileName || `${item.title}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      // Optionally bump the download counter
      setSongs((prev) =>
        prev.map((s) =>
          s.id === item.id ? { ...s, downloads: s.downloads + 1 } : s
        )
      );
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  // Clean up all audio on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
    };
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ---------- SEARCH + FILTER LOGIC ----------
  const filteredSongs = songs.filter((item) => {
    const searchLower = search.trim().toLowerCase();

    const matchesSearch =
      searchLower === "" ||
      item.title.toLowerCase().includes(searchLower) ||
      item.fileName.toLowerCase().includes(searchLower);

    const matchesStatus = status === "All Status" || item.status === status;

    // Date filter: "This Month" checks dateAdded month/year against today
    let matchesDate = true;
    if (dateFilter === "This Month") {
      const today = new Date();
      const [day, month, year] = item.dateAdded.split("-").map(Number);
      matchesDate =
        month === today.getMonth() + 1 && year === today.getFullYear();
    } else if (dateFilter === "This Year") {
      const today = new Date();
      const [, , year] = item.dateAdded.split("-").map(Number);
      matchesDate = year === today.getFullYear();
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({ ...item });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = (e) => {
    e.preventDefault();
    setSongs((prev) =>
      prev.map((item) => (item.id === editItem.id ? editItem : item))
    );
    closeEditModal();
  };

  // ---------- STATS ----------
  const totalSongs = songs.length;
  const totalPlays = songs.reduce((sum, s) => sum + s.plays, 0);
  const totalDownloads = songs.reduce((sum, s) => sum + s.downloads, 0);

  const recentlyAdded = songs.filter((s) => {
    const today = new Date();
    const [day, month, year] = s.dateAdded.split("-").map(Number);
    return month === today.getMonth() + 1 && year === today.getFullYear();
  }).length;

  return (
    <div className="mp3123-page">
      {/* Header */}
      <div className="mp3123-header">
        <div className="mp3123-header-left">
          <div className="mp3123-page-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18V5l12-2v13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div>
            <h2 className="mp3123-title">MP3 Songs</h2>
            <p className="mp3123-subtitle">
              Manage and organize all your MP3 songs
            </p>
          </div>
        </div>

        <button
          className="mp3123-add-btn"
          onClick={() => navigate("/AdminDashboard/mp3-form")}
        >
          <span className="mp3123-add-icon">+</span> Add MP3 Song
        </button>
      </div>

      {/* Stat Cards */}
      <div className="mp3123-stats-grid">
        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18V5l12-2v13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Total Songs</span>
            <span className="mp3123-stat-value">{totalSongs}</span>
            <span className="mp3123-stat-sub">All time songs</span>
          </div>
        </div>

        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 15v-3a8 8 0 0116 0v3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <rect x="2.5" y="15" width="4" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
              <rect x="17.5" y="15" width="4" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Total Plays</span>
            <span className="mp3123-stat-value">
              {totalPlays.toLocaleString()}
            </span>
            <span className="mp3123-stat-sub">All time plays</span>
          </div>
        </div>

        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Recently Added</span>
            <span className="mp3123-stat-value">{recentlyAdded}</span>
            <span className="mp3123-stat-sub">This month</span>
          </div>
        </div>

        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3v12m0 0l-4-4m4 4l4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Total Downloads</span>
            <span className="mp3123-stat-value">
              {totalDownloads.toLocaleString()}
            </span>
            <span className="mp3123-stat-sub">All time downloads</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mp3123-filters">
        <div className="mp3123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by song title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="mp3123-select"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Dates</option>
          <option>This Month</option>
          <option>This Year</option>
        </select>

        <select
          className="mp3123-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <button className="mp3123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="mp3123-table-wrapper">
        <table className="mp3123-table  table table-hover">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Song Title</th>
              <th>File Name</th>
              <th>Duration</th>
              <th>Date Added</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredSongs.length === 0 ? (
              <tr>
                <td colSpan="7" className="mp3123-no-data">
                  No songs found.
                </td>
              </tr>
            ) : (
              filteredSongs.map((item, index) => {
                const isPlaying = playingId === item.id;
                const progressPercent =
                  isPlaying && duration > 0
                    ? (currentTime / duration) * 100
                    : 0;

                return (
                  <tr key={item.id}>
                    <td>{index + 1}</td>

                    <td>
                      <div className="mp3123-song-cell">
                        <div className={`mp3123-cover ${item.color}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M9 18V5l12-2v13"
                              stroke="#fff"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle cx="6" cy="18" r="3" stroke="#fff" strokeWidth="1.8" />
                            <circle cx="18" cy="16" r="3" stroke="#fff" strokeWidth="1.8" />
                          </svg>
                        </div>

                        <div className="mp3123-song-info">
                          <div className="mp3123-song-title">{item.title}</div>
                          <div className="mp3123-player">
                            <button
                              className="mp3123-play-btn"
                              onClick={() => togglePlay(item)}
                            >
                              {isPlaying ? (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                  <rect x="5" y="4" width="5" height="16" rx="1" />
                                  <rect x="14" y="4" width="5" height="16" rx="1" />
                                </svg>
                              ) : (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 4l14 8-14 8V4z" />
                                </svg>
                              )}
                            </button>

                            <span className="mp3123-player-time">
                              {isPlaying ? formatTime(currentTime) : "0:00"}
                            </span>

                            <div className="mp3123-progress-track">
                              <div
                                className="mp3123-progress-fill"
                                style={{
                                  width: `${progressPercent}%`,
                                }}
                              />
                            </div>

                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="mp3123-volume-icon">
                              <path
                                d="M5 9v6h4l5 4V5L9 9H5z"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinejoin="round"
                              />
                            </svg>

                            {/* Download Icon Button */}
                            <button
                              className="mp3123-download-btn"
                              onClick={() => handleDownload(item)}
                              title={`Download ${item.fileName}`}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="mp3123-file-badge">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinejoin="round"
                          />
                          <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        </svg>
                        {item.fileName}
                      </span>
                    </td>

                    <td>
                      <div className="mp3123-duration-cell">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {item.duration}
                      </div>
                    </td>

                    <td>
                      <div className="mp3123-date-cell">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        {item.dateAdded}
                      </div>
                    </td>

                    <td>
                      <span
                        className={`mp3123-status-badge ${
                          item.status === "Active"
                            ? "mp3123-status-active"
                            : "mp3123-status-inactive"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td>
                      <button className="mp3123-edit-btn" onClick={() => openEditModal(item)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mp3123-pagination-bar">
        <span className="mp3123-pagination-info">
          Showing 1 to {filteredSongs.length} of {totalSongs} songs
        </span>

        <div className="mp3123-pagination-controls">
          <button
            className="mp3123-page-arrow"
            onClick={() => handlePageChange(currentPage - 1)}
          >
            &lt;
          </button>

          {[1, 2, 3].map((p) => (
            <button
              key={p}
              className={`mp3123-page-num ${currentPage === p ? "mp3123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <span className="mp3123-page-dots">...</span>

          <button
            className={`mp3123-page-num ${currentPage === totalPages ? "mp3123-active" : ""}`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>

          <button
            className="mp3123-page-arrow"
            onClick={() => handlePageChange(currentPage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="mp3123-modal-overlay" onClick={closeEditModal}>
          <div className="mp3123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mp3123-modal-header">
              <h3>Edit MP3 Song</h3>
              <button className="mp3123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="mp3123-modal-body" onSubmit={handleEditSave}>
              <div className="mp3123-form-group">
                <label>Song Title</label>
                <input
                  type="text"
                  name="title"
                  value={editItem.title}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="mp3123-form-group">
                <label>File Name</label>
                <input
                  type="text"
                  name="fileName"
                  value={editItem.fileName}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="mp3123-form-row">
                <div className="mp3123-form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    name="duration"
                    placeholder="MM:SS"
                    value={editItem.duration}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="mp3123-form-group">
                  <label>Date Added</label>
                  <input
                    type="text"
                    name="dateAdded"
                    placeholder="DD-MM-YYYY"
                    value={editItem.dateAdded}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="mp3123-form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={editItem.status}
                  onChange={handleEditChange}
                >
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>

              <div className="mp3123-modal-footer">
                <button
                  type="button"
                  className="mp3123-btn-cancel"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="mp3123-btn-save">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMp3Table;